/**
 * ApplicationTracking Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useApplicationTrackingStore } from '../../store/application-tracking.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ApplicationTrackingIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch application trackings and update store
     */
    async fetchApplicationTrackings(params = {}) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const page = params.page || store.paginationData?.currentPage || 1;
            const pageSize = params.pageSize || store.paginationData?.itemsPerPage || 10;
            const search = params.search !== undefined ? params.search : store.searchQuery;
            const sortBy = params.sortBy || '';
            const sortOrder = params.sortOrder || 'desc';
            const columnFilters = params.columnFilters !== undefined ? params.columnFilters : (store.columnFilters || {});

            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setApplicationTrackings(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            if (store.setError) store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch by ID
     */
    async fetchApplicationTrackingById(id) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getById(id);
            if (store.setSelectedApplication) store.setSelectedApplication(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createApplicationTracking(data) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('gdprApplicationTracking:messages.createSuccess', { defaultValue: 'Channel register entry created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateApplicationTracking(id, data) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('gdprApplicationTracking:messages.updateSuccess', { defaultValue: 'Channel register entry updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteApplicationTracking(id) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('gdprApplicationTracking:messages.deleteSuccess', { defaultValue: 'Channel register entry deleted' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Batch delete items
     */
    async batchDeleteApplicationTrackings(ids) {
        const store = useApplicationTrackingStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('gdprApplicationTracking:messages.bulkDeleteSuccess', {
                defaultValue: 'Selected channel register entries deleted',
                count: ids.length,
            }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async promoteToDsr(id, data = {}) {
        const store = useApplicationTrackingStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.promoteToDsr(id, data);
            toast.success(
                i18next.t('gdprApplicationTracking:messages.promoteSuccess', {
                    defaultValue: 'Promoted to data subject request',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
